import React, { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * Sanitize common escape sequence artifacts in LaTeX strings produced by LLMs/JSON:
 * e.g. literal tabs \t instead of \\t in \theta, \tan, \tau, \times, \text, \tilde
 * or form feeds \f in \frac, \b in \beta, \r in \rho.
 */
function sanitizeLatex(str) {
  if (!str || typeof str !== 'string') return '';
  return str
    // Fix JSON tab escape before LaTeX command names
    .replace(/\t(heta|an|au|imes|ext|ilde|frac)/g, '\\$1')
    // Fix JSON form feed escape before \frac
    .replace(/\f(rac)/g, '\\$1')
    // Fix JSON backspace escape before \beta
    .replace(/\b(eta)/g, '\\$1')
    // Fix JSON carriage return escape before \rho
    .replace(/\r(ho)/g, '\\$1');
}

/**
 * Splits text into plain text chunks and LaTeX equation chunks ($...$, $$...$$, \(...\), \[...\])
 * and renders LaTeX chunks via KaTeX.
 */
export default function MathText({ text, className = '' }) {
  const renderedElements = useMemo(() => {
    if (!text || typeof text !== 'string') return text || null;

    const sanitized = sanitizeLatex(text);

    // Regex matching display math ($$...$$ or \[...\]) and inline math ($...$ or \(...\))
    const mathRegex = /(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]|\$[^$\n]+?\$|\\\([\s\S]*?\\\))/g;

    const parts = sanitized.split(mathRegex);

    return parts.map((part, idx) => {
      if (!part) return null;

      let isDisplay = false;
      let mathContent = null;

      if (part.startsWith('$$') && part.endsWith('$$')) {
        isDisplay = true;
        mathContent = part.slice(2, -2).trim();
      } else if (part.startsWith('\\[') && part.endsWith('\\]')) {
        isDisplay = true;
        mathContent = part.slice(2, -2).trim();
      } else if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
        isDisplay = false;
        mathContent = part.slice(1, -1).trim();
      } else if (part.startsWith('\\(') && part.endsWith('\\)')) {
        isDisplay = false;
        mathContent = part.slice(2, -2).trim();
      }

      if (mathContent != null) {
        try {
          const html = katex.renderToString(mathContent, {
            throwOnError: false,
            displayMode: isDisplay,
            strict: false
          });
          return (
            <span
              key={idx}
              className={`math-rendered ${isDisplay ? 'math-display' : 'math-inline'}`}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          );
        } catch (err) {
          // Fallback to raw text if KaTeX fails
          return <span key={idx} className="math-fallback">{part}</span>;
        }
      }

      // Regular text: render as span or text
      return <React.Fragment key={idx}>{part}</React.Fragment>;
    });
  }, [text]);

  if (!text) return null;

  return <span className={`math-text-wrapper ${className}`}>{renderedElements}</span>;
}
